// SimpleConfigForm.jsx with Firebase managedBikes Integration
import React, { useState, useEffect } from 'react';
import { Globe, MessageSquare, Mail, BarChart, Map, DollarSign, Plus, Trash2, ChevronDown, MapPin, Navigation, Database, Save, Bike } from 'lucide-react';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  Timestamp,
  query,
  getDocs,
  where
} from 'firebase/firestore';

// ✅ REMOVED: Legacy bikeData import
// ❌ import { getBikeModels } from '../bikeData';

// ✅ NEW: Import Firebase bike utilities
import { getBikeModelsFromFirebase, invalidateBikeModelsCache } from '../Configuration/distanceUtils';

const SimpleConfigForm = ({ currentCompany, getCompanyContext }) => {
  const [activeTab, setActiveTab] = useState('messaging');
  const [config, setConfig] = useState({
    // Messaging API settings
    whatsappWebhookToken: '',
    emailApiKey: '',
    
    // Google Analytics settings
    gaPropertyId: 'YOUR_GA_PROPERTY_ID',
    
    // Google Maps API Key
    googleMapsApiKey: '',
    
    // Rental Office Location
    rentalOfficeLocation: {
      address: '',
      latitude: null,
      longitude: null,
      placeId: ''
    },
    
    // Delivery pricing settings
    deliveryPricing: []
  });
  
  // Firebase connection states
  const [firebaseConnected, setFirebaseConnected] = useState(false);
  const [configCollection] = useState(collection(db, 'configurations'));
  const [configDocId] = useState('app_config'); // Single document for app configuration
  
  // ✅ NEW: Firebase bike models from managedBikes collection
  const [bikeModels, setBikeModels] = useState([]);
  const [bikeModelsLoading, setBikeModelsLoading] = useState(false);
  const [bikeModelsError, setBikeModelsError] = useState('');
  
  // Company context
  const companyId = getCompanyContext ? getCompanyContext() : currentCompany?.id || 'default';
  
  // New bike model input for delivery pricing
  const [selectedBikeModel, setSelectedBikeModel] = useState('');
  const [newDeliveryPrice, setNewDeliveryPrice] = useState('');
  const [newMinimumCharge, setNewMinimumCharge] = useState('5'); // Default to $5
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  // State for location lookup
  const [locationLookup, setLocationLookup] = useState('');
  const [locationSearching, setLocationSearching] = useState(false);
  const [locationResults, setLocationResults] = useState([]);
  const [locationError, setLocationError] = useState('');
  
  const [saved, setSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // ✅ NEW: Load bike models from Firebase managedBikes collection
  const loadBikeModelsFromFirebase = async () => {
    setBikeModelsLoading(true);
    setBikeModelsError('');
    
    try {
      console.log(`🚗 Loading bike models from Firebase for company: ${companyId}`);
      
      // Get bike models from Firebase managedBikes collection
      const models = await getBikeModelsFromFirebase(companyId);
      
      if (models.length === 0) {
        // If no models found for this company, try loading all models as fallback
        console.warn(`No bike models found for company ${companyId}, loading all models as fallback`);
        const allModels = await getBikeModelsFromFirebase();
        setBikeModels(allModels);
        
        if (allModels.length === 0) {
          setBikeModelsError('No bikes found in Firebase. Please add bikes to your fleet in Bike Management first.');
        } else {
          console.log(`✅ Loaded ${allModels.length} bike models from all companies as fallback`);
        }
      } else {
        setBikeModels(models);
        console.log(`✅ Loaded ${models.length} bike models for company ${companyId}:`, models);
      }
      
    } catch (error) {
      console.error('Error loading bike models from Firebase:', error);
      setBikeModelsError('Failed to load bike models from Firebase. Please check your connection.');
      setBikeModels([]); // Set empty array as fallback
    } finally {
      setBikeModelsLoading(false);
    }
  };

  // ✅ NEW: Refresh bike models cache and reload
  const refreshBikeModels = async () => {
    invalidateBikeModelsCache();
    await loadBikeModelsFromFirebase();
  };

  // Load existing configuration and bike models on component mount
  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      try {
        // Test Firebase connection and load configuration
        await loadConfigurationFromFirebase();
        setFirebaseConnected(true);
        
        // ✅ NEW: Load bike models from Firebase managedBikes collection
        await loadBikeModelsFromFirebase();
        
        console.log('Firebase configuration and bike models loaded successfully');
      } catch (error) {
        console.error('Error loading configuration or bike models:', error);
        setFirebaseConnected(false);
        setError('Failed to connect to Firebase. Please check your connection.');
        
        // Still try to load bike models even if general config fails
        try {
          await loadBikeModelsFromFirebase();
        } catch (bikeError) {
          console.error('Error loading bike models from Firebase:', bikeError);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [companyId]);

  // Load configuration from Firebase
  const loadConfigurationFromFirebase = async () => {
    try {
      const configDocRef = doc(configCollection, configDocId);
      const configDoc = await getDoc(configDocRef);
      
      if (configDoc.exists()) {
        const firebaseConfig = configDoc.data();
        
        // Ensure all required properties exist
        const loadedConfig = {
          whatsappWebhookToken: firebaseConfig.whatsappWebhookToken || '',
          emailApiKey: firebaseConfig.emailApiKey || '',
          gaPropertyId: firebaseConfig.gaPropertyId || 'YOUR_GA_PROPERTY_ID',
          googleMapsApiKey: firebaseConfig.googleMapsApiKey || '',
          rentalOfficeLocation: firebaseConfig.rentalOfficeLocation || {
            address: '',
            latitude: null,
            longitude: null,
            placeId: ''
          },
          deliveryPricing: firebaseConfig.deliveryPricing || []
        };
        
        setConfig(loadedConfig);
        console.log('Configuration loaded from Firebase:', loadedConfig);
      } else {
        console.log('No configuration found in Firebase, using defaults');
        // Create default configuration in Firebase
        await saveConfigurationToFirebase(config);
      }
    } catch (error) {
      console.error('Error loading configuration from Firebase:', error);
      throw error;
    }
  };

  // Save configuration to Firebase
  const saveConfigurationToFirebase = async (configToSave = config) => {
    try {
      const configDocRef = doc(configCollection, configDocId);
      
      const configData = {
        ...configToSave,
        lastUpdated: Timestamp.now(),
        version: '1.0'
      };
      
      await setDoc(configDocRef, configData);
      console.log('Configuration saved to Firebase successfully');
      
      // Also update global variables for compatibility
      window.RENTAL_OFFICE_LOCATION = configToSave.rentalOfficeLocation;
      window.GOOGLE_MAPS_API_KEY = configToSave.googleMapsApiKey;
      
      return true;
    } catch (error) {
      console.error('Error saving configuration to Firebase:', error);
      throw error;
    }
  };

  // Effect to initialize Google Maps once API key is available
  useEffect(() => {
    if (config.googleMapsApiKey && typeof window.google === 'undefined') {
      // Load Google Maps API
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${config.googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('Google Maps API loaded');
      };
      document.head.appendChild(script);
    }
  }, [config.googleMapsApiKey]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle location lookup input change
  const handleLocationLookupChange = (e) => {
    setLocationLookup(e.target.value);
    setLocationError('');
  };

  // Search for location using Google Places API
  const searchLocation = () => {
    if (!locationLookup.trim()) {
      setLocationError('Please enter a location to search');
      return;
    }
    
    if (!config.googleMapsApiKey) {
      setLocationError('Google Maps API key is required. Please configure it in the Maps tab first.');
      return;
    }
    
    if (typeof window.google === 'undefined') {
      setLocationError('Google Maps API is not loaded yet. Please check your API key and try again.');
      return;
    }
    
    setLocationSearching(true);
    setLocationError('');
    
    // Create a PlacesService if it doesn't exist
    const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
    
    // Perform a text search
    placesService.textSearch({
      query: locationLookup
    }, (results, status) => {
      setLocationSearching(false);
      
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setLocationResults(results.slice(0, 5)); // Limit to top 5 results
      } else {
        setLocationError('Location search failed. Please try a different search term.');
        setLocationResults([]);
      }
    });
  };

  // Select a location from search results
  const selectLocation = (place) => {
    setConfig(prev => ({
      ...prev,
      rentalOfficeLocation: {
        address: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        placeId: place.place_id
      }
    }));
    
    setLocationResults([]);
    setLocationLookup('');
  };

  // Clear selected location
  const clearLocation = () => {
    setConfig(prev => ({
      ...prev,
      rentalOfficeLocation: {
        address: '',
        latitude: null,
        longitude: null,
        placeId: ''
      }
    }));
  };

  // Use current location (geolocation API)
  const useCurrentLocation = () => {
    if (navigator.geolocation) {
      setLocationSearching(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          
          // Reverse geocode to get address
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
            setLocationSearching(false);
            
            if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
              setConfig(prev => ({
                ...prev,
                rentalOfficeLocation: {
                  address: results[0].formatted_address,
                  latitude,
                  longitude,
                  placeId: results[0].place_id
                }
              }));
            } else {
              setLocationError('Could not determine address from your location.');
            }
          });
        },
        (error) => {
          setLocationSearching(false);
          setLocationError(`Geolocation error: ${error.message}`);
        }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser.');
    }
  };

  // ✅ UPDATED: Add new delivery pricing model using Firebase bike models
  const handleAddDeliveryPricing = () => {
    if (!selectedBikeModel || !newDeliveryPrice || isNaN(parseFloat(newDeliveryPrice))) {
      alert('Please select a bike model and enter a valid price per km');
      return;
    }
    
    if (!newMinimumCharge || isNaN(parseFloat(newMinimumCharge))) {
      alert('Please enter a valid minimum charge');
      return;
    }
    
    // Check if this model already has a pricing entry
    const modelExists = config.deliveryPricing.some(
      item => item.model.toUpperCase() === selectedBikeModel.toUpperCase()
    );
    
    if (modelExists) {
      alert(`Pricing for ${selectedBikeModel} already exists. Please edit the existing entry or choose a different model.`);
      return;
    }
    
    const newPrice = {
      model: selectedBikeModel,
      pricePerKm: parseFloat(newDeliveryPrice),
      minimumCharge: parseFloat(newMinimumCharge)
    };
    
    setConfig(prev => ({
      ...prev,
      deliveryPricing: [...prev.deliveryPricing, newPrice]
    }));
    
    // Clear input fields
    setSelectedBikeModel('');
    setNewDeliveryPrice('');
    setNewMinimumCharge('5'); // Reset to default
    
    console.log(`✅ Added delivery pricing for Firebase bike model: ${selectedBikeModel}`);
  };
  
  // Remove a delivery pricing model
  const handleRemoveDeliveryPricing = (indexToRemove) => {
    const removedModel = config.deliveryPricing[indexToRemove];
    setConfig(prev => ({
      ...prev,
      deliveryPricing: prev.deliveryPricing.filter((_, index) => index !== indexToRemove)
    }));
    console.log(`🗑️ Removed delivery pricing for model: ${removedModel?.model}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!firebaseConnected) {
      setError('Cannot save configuration: Firebase is not connected.');
      return;
    }
    
    // Validate rental office location if delivery pricing is configured
    if (config.deliveryPricing.length > 0 && 
        (!config.rentalOfficeLocation.latitude || !config.rentalOfficeLocation.longitude)) {
      alert('Please set your rental office location before saving delivery pricing configuration.');
      setActiveTab('maps'); // Switch to maps tab to set location
      return;
    }
    
    setIsSaving(true);
    setError('');
    
    try {
      await saveConfigurationToFirebase(config);
      
      // Show success message
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving configuration:', error);
      setError('Failed to save configuration to Firebase. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Test Firebase connection
  const testFirebaseConnection = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      await loadConfigurationFromFirebase();
      setFirebaseConnected(true);
      setError('');
      alert('Firebase connection successful!');
    } catch (error) {
      setFirebaseConnected(false);
      setError('Firebase connection failed. Please check your Firebase configuration.');
      console.error('Firebase test failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-8">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
            <p className="text-gray-300">Loading configuration from Firebase...</p>
            {currentCompany && (
              <p className="text-gray-400 text-sm">Company: {currentCompany.name}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Firebase Connection Status */}
      {!firebaseConnected && (
        <div className="mb-4 bg-red-800 border border-red-600 text-white p-3 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Database className="w-5 h-5 mr-2" />
              <span>⚠️ Firebase connection failed. Please check your Firebase configuration.</span>
            </div>
            <button
              onClick={testFirebaseConnection}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-4 bg-red-800 border border-red-600 text-white p-3 rounded-lg">
          <p>{error}</p>
          <button 
            className="mt-2 bg-red-600 hover:bg-red-700 text-white rounded px-3 py-1 text-sm"
            onClick={() => setError('')}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ✅ NEW: Company Context Banner */}
      {currentCompany && (
        <div className="mb-4 bg-blue-900/30 border border-blue-700 text-blue-300 p-3 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Bike className="w-5 h-5 mr-2" />
              <span>📊 Configuration for: <strong>{currentCompany.name}</strong> • Bike Models: {bikeModels.length}</span>
            </div>
            <button
              onClick={refreshBikeModels}
              disabled={bikeModelsLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
            >
              {bikeModelsLoading ? 'Loading...' : '🔄 Refresh Bikes'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden border border-gray-700">
        {/* Tabs */}
        <div className="flex flex-wrap border-b border-gray-700">
          <button
            className={`flex items-center px-4 py-3 ${activeTab === 'messaging' 
              ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white' 
              : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}
            onClick={() => setActiveTab('messaging')}
          >
            <MessageSquare size={18} className="mr-2" />
            Messaging
          </button>
          <button
            className={`flex items-center px-4 py-3 ${activeTab === 'firebase' 
              ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white' 
              : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}
            onClick={() => setActiveTab('firebase')}
          >
            <Database size={18} className="mr-2" />
            Firebase
          </button>
          <button
            className={`flex items-center px-4 py-3 ${activeTab === 'maps' 
              ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white' 
              : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}
            onClick={() => setActiveTab('maps')}
          >
            <Map size={18} className="mr-2" />
            Maps & Location
          </button>
          <button
            className={`flex items-center px-4 py-3 ${activeTab === 'delivery' 
              ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white' 
              : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}
            onClick={() => setActiveTab('delivery')}
          >
            <DollarSign size={18} className="mr-2" />
            Delivery Pricing
          </button>
          <button
            className={`flex items-center px-4 py-3 ${activeTab === 'analytics' 
              ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white' 
              : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}
            onClick={() => setActiveTab('analytics')}
          >
            <BarChart size={18} className="mr-2" />
            Analytics
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {/* Messaging API Configuration */}
          {activeTab === 'messaging' && (
            <div>
              <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                <MessageSquare size={20} className="mr-2 text-orange-500" />
                Messaging API Configuration
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-gray-300 mb-2" htmlFor="whatsappWebhookToken">
                    WhatsApp Webhook Verification Token
                  </label>
                  <input
                    type="text"
                    id="whatsappWebhookToken"
                    name="whatsappWebhookToken"
                    value={config.whatsappWebhookToken}
                    onChange={handleChange}
                    className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white"
                    placeholder="Enter webhook verification token"
                  />
                  <p className="text-gray-400 text-xs mt-1">
                    This token is used for verifying WhatsApp Business API webhooks
                  </p>
                </div>
                
                <div>
                  <label className="block text-gray-300 mb-2" htmlFor="emailApiKey">
                    Email Service API Key
                  </label>
                  <input
                    type="text"
                    id="emailApiKey"
                    name="emailApiKey"
                    value={config.emailApiKey}
                    onChange={handleChange}
                    className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white"
                    placeholder="Enter email service API key"
                  />
                  <p className="text-gray-400 text-xs mt-1">
                    API key for your email service provider (SendGrid, Mailchimp, etc.)
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Firebase Configuration */}
          {activeTab === 'firebase' && (
            <div>
              <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                <Database size={20} className="mr-2 text-orange-500" />
                Firebase Configuration Status
              </h2>
              
              <div className="space-y-6">
                <div className="bg-gray-700 rounded-lg border border-gray-600 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-2 ${firebaseConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <h3 className="text-white font-semibold">Firebase Connection Status</h3>
                    </div>
                    <button
                      type="button"
                      onClick={testFirebaseConnection}
                      disabled={isLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded text-sm transition-colors"
                    >
                      {isLoading ? 'Testing...' : 'Test Connection'}
                    </button>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-300">
                      Status: <span className={firebaseConnected ? 'text-green-400' : 'text-red-400'}>
                        {firebaseConnected ? 'Connected' : 'Disconnected'}
                      </span>
                    </p>
                    <p className="text-gray-300">
                      Configuration Collection: <span className="text-blue-400">configurations</span>
                    </p>
                    <p className="text-gray-300">
                      Bike Models Collection: <span className="text-blue-400">managedBikes</span>
                    </p>
                    <p className="text-gray-300">
                      Document ID: <span className="text-blue-400">{configDocId}</span>
                    </p>
                    {currentCompany && (
                      <p className="text-gray-300">
                        Company Context: <span className="text-orange-400">{currentCompany.name}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* ✅ NEW: Bike Models Status */}
                <div className="bg-gray-700 rounded-lg border border-gray-600 p-4">
                  <h3 className="text-white font-semibold mb-3 flex items-center">
                    <Bike className="mr-2" size={18} />
                    Firebase Bike Models Status
                  </h3>
                  
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-300">
                      Models Loaded: <span className={bikeModels.length > 0 ? 'text-green-400' : 'text-yellow-400'}>
                        {bikeModels.length} model{bikeModels.length !== 1 ? 's' : ''}
                      </span>
                    </p>
                    
                    {bikeModelsError && (
                      <p className="text-red-400">{bikeModelsError}</p>
                    )}
                    
                    {bikeModels.length > 0 && (
                      <div className="mt-2">
                        <p className="text-gray-400 text-xs mb-1">Available Models:</p>
                        <div className="flex flex-wrap gap-1">
                          {bikeModels.slice(0, 10).map((model, index) => (
                            <span key={index} className="bg-blue-600 text-white px-2 py-1 rounded text-xs">
                              {model}
                            </span>
                          ))}
                          {bikeModels.length > 10 && (
                            <span className="text-gray-400 text-xs px-2 py-1">
                              +{bikeModels.length - 10} more...
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-700 rounded-lg border border-gray-600 p-4">
                  <h3 className="text-white font-semibold mb-3">Firebase Integration Features</h3>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Configuration stored in Firestore
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      ✅ Bike models from managedBikes collection
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Real-time configuration updates
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Centralized settings management
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Company-specific bike model filtering
                    </li>
                  </ul>
                </div>

                <div className="bg-blue-900/30 rounded-lg border border-blue-800 p-4">
                  <p className="text-blue-300 text-sm">
                    <strong>✅ Firebase Integration Complete:</strong> All configurations and bike models are now stored in Firebase Firestore. 
                    Bike models are automatically loaded from your managed bikes collection, ensuring delivery pricing stays in sync with your actual fleet.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Google Maps & Location Configuration */}
          {activeTab === 'maps' && (
            <div>
              <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                <Map size={20} className="mr-2 text-orange-500" />
                Maps & Location Configuration
              </h2>
              
              <div className="space-y-6">
                {/* Google Maps API Key */}
                <div>
                  <label className="block text-gray-300 mb-2" htmlFor="googleMapsApiKey">
                    Google Maps API Key
                  </label>
                  <input
                    type="text"
                    id="googleMapsApiKey"
                    name="googleMapsApiKey"
                    value={config.googleMapsApiKey}
                    onChange={handleChange}
                    className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white"
                    placeholder="Enter your Google Maps API key"
                  />
                  <p className="text-gray-400 text-xs mt-1">
                    Required for the location picker and delivery distance calculations
                  </p>
                </div>
                
                {/* Rental Office Location Section */}
                <div className="bg-gray-700 rounded-lg border border-gray-600 p-4 mt-6">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                    <MapPin size={18} className="mr-2 text-orange-500" />
                    Rental Office Location
                  </h3>
                  
                  <p className="text-gray-300 text-sm mb-4">
                    Set your rental office location to calculate delivery/collection distances and fees.
                    This location will be used as the starting point for all distance calculations.
                  </p>
                  
                  {/* Current Location Display */}
                  {config.rentalOfficeLocation.address && (
                    <div className="mb-4 p-3 bg-gray-800 rounded-lg border border-gray-600">
                      <div className="flex items-start mb-2">
                        <MapPin size={18} className="text-orange-500 mr-2 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="text-white font-medium">Current Office Location</h4>
                          <p className="text-gray-300 text-sm">{config.rentalOfficeLocation.address}</p>
                          {config.rentalOfficeLocation.latitude && config.rentalOfficeLocation.longitude && (
                            <p className="text-gray-400 text-xs mt-1">
                              Coordinates: {config.rentalOfficeLocation.latitude.toFixed(6)}, {config.rentalOfficeLocation.longitude.toFixed(6)}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={clearLocation}
                        className="text-red-400 hover:text-red-300 text-sm flex items-center"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Clear Location
                      </button>
                    </div>
                  )}
                  
                  {/* Location Search */}
                  <div className="mb-3">
                    <label className="block text-gray-300 text-sm mb-2">Search for your office location</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={locationLookup}
                        onChange={handleLocationLookupChange}
                        placeholder="Enter address or landmark"
                        className="flex-1 bg-gray-800 border border-gray-600 rounded p-2 text-white"
                        disabled={!config.googleMapsApiKey || locationSearching}
                      />
                      <button
                        type="button"
                        onClick={searchLocation}
                        disabled={!config.googleMapsApiKey || locationSearching || !locationLookup.trim()}
                        className={`${
                          !config.googleMapsApiKey || locationSearching || !locationLookup.trim()
                            ? 'bg-gray-600 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                        } text-white px-4 py-2 rounded transition-colors`}
                      >
                        {locationSearching ? (
                          <span className="flex items-center">
                            <span className="animate-spin h-4 w-4 mr-2 border-t-2 border-b-2 border-white rounded-full"></span>
                            Searching...
                          </span>
                        ) : 'Search'}
                      </button>
                    </div>
                    
                    {/* Use current location button */}
                    <button
                      type="button"
                      onClick={useCurrentLocation}
                      disabled={!config.googleMapsApiKey || locationSearching}
                      className={`mt-2 ${
                        !config.googleMapsApiKey || locationSearching
                          ? 'text-gray-500 cursor-not-allowed'
                          : 'text-blue-400 hover:text-blue-300'
                      } text-sm flex items-center`}
                    >
                      <Navigation size={14} className="mr-1" />
                      Use my current location
                    </button>
                  </div>
                  
                  {/* Location Error */}
                  {locationError && (
                    <div className="mb-3 p-2 bg-red-900/30 border border-red-800 rounded text-red-300 text-sm">
                      {locationError}
                    </div>
                  )}
                  
                  {/* Location Results */}
                  {locationResults.length > 0 && (
                    <div className="mt-3 border border-gray-600 rounded-lg overflow-hidden">
                      <h4 className="bg-gray-800 px-3 py-2 text-white text-sm font-medium">Search Results</h4>
                      <div className="max-h-60 overflow-y-auto">
                        {locationResults.map((place, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => selectLocation(place)}
                            className="w-full text-left px-3 py-2 border-t border-gray-600 hover:bg-gray-600 text-gray-300 text-sm transition-colors flex items-start"
                          >
                            <MapPin size={16} className="text-orange-500 mr-2 mt-1 flex-shrink-0" />
                            <div>
                              <div className="font-medium text-white">{place.name}</div>
                              <div className="text-gray-400 text-xs">{place.formatted_address}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Location Help Text */}
                  {!config.googleMapsApiKey && (
                    <div className="mt-3 p-3 bg-blue-900/30 border border-blue-800 rounded text-blue-300 text-sm">
                      Please enter a Google Maps API key above before configuring your office location.
                    </div>
                  )}
                </div>
                
                <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                  <h3 className="text-white font-medium mb-2">Required Google Maps API Services</h3>
                  <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                    <li>Maps JavaScript API</li>
                    <li>Geocoding API</li>
                    <li>Places API</li>
                    <li>Distance Matrix API (for delivery fee calculation)</li>
                  </ul>
                  <p className="text-gray-400 text-xs mt-3">
                    Make sure these services are enabled for your API key in the Google Cloud Console
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* ✅ UPDATED: Delivery Pricing Configuration with Firebase bike models */}
          {activeTab === 'delivery' && (
            <div>
              <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                <DollarSign size={20} className="mr-2 text-orange-500" />
                Delivery Fee Configuration
              </h2>
              
              <div className="space-y-6">
                <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                  <p className="text-gray-300 text-sm">
                    Configure the price per kilometer and minimum charge for delivery/collection fees when customers select custom locations. 
                    Each bike model can have a different rate and minimum charge.
                  </p>
                  <p className="text-blue-300 text-xs mt-2">
                    ✅ Bike models are automatically loaded from your Firebase managed bikes collection.
                  </p>
                </div>
                
                {/* Bike Models Status */}
                {bikeModelsError && (
                  <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg">
                    <p className="text-red-300 text-sm flex items-start">
                      <Bike size={18} className="mr-2 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Bike models not available:</strong> {bikeModelsError}
                      </span>
                    </p>
                    <button
                      type="button"
                      onClick={refreshBikeModels}
                      className="mt-2 text-red-300 hover:text-red-200 text-sm underline"
                    >
                      Try loading again
                    </button>
                  </div>
                )}
                
                {bikeModels.length === 0 && !bikeModelsError && (
                  <div className="p-4 bg-yellow-900/30 border border-yellow-700 rounded-lg">
                    <p className="text-yellow-300 text-sm flex items-start">
                      <Bike size={18} className="mr-2 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>No bike models found:</strong> Please add bikes to your fleet in Bike Management before configuring delivery pricing.
                      </span>
                    </p>
                  </div>
                )}
                
                {/* Location warning */}
                {(!config.rentalOfficeLocation.latitude || !config.rentalOfficeLocation.longitude) && (
                  <div className="p-4 bg-orange-900/30 border border-orange-700 rounded-lg">
                    <p className="text-orange-300 text-sm flex items-start">
                      <MapPin size={18} className="mr-2 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Office location not set:</strong> Please configure your rental office location in the Maps & Location tab before setting up delivery pricing.
                      </span>
                    </p>
                    <button
                      type="button"
                      onClick={() => setActiveTab('maps')}
                      className="mt-2 text-orange-300 hover:text-orange-200 text-sm underline"
                    >
                      Go to Maps & Location settings
                    </button>
                  </div>
                )}
                
                {/* Add new delivery pricing with Firebase bike models dropdown */}
                {bikeModels.length > 0 && (
                  <div className="border border-gray-600 p-4 rounded-lg">
                    <h3 className="text-white text-lg mb-3">Add New Price</h3>
                    <div className="grid grid-cols-1 gap-3">
                      {/* Bike Model Selector - Firebase Models */}
                      <div className="relative">
                        <label className="block text-gray-300 mb-1 text-sm">
                          Bike Model <span className="text-blue-400">(from Firebase)</span>
                        </label>
                        <div className="relative">
                          <button
                            type="button"
                            className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white flex justify-between items-center"
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                          >
                            {selectedBikeModel ? selectedBikeModel : 'Select a bike model'}
                            <ChevronDown size={16} className={`transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {dropdownOpen && (
                            <div className="absolute z-10 w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg max-h-60 overflow-y-auto">
                              {bikeModels.map((model, index) => {
                                // Check if this model already has pricing
                                const isConfigured = config.deliveryPricing.some(
                                  item => item.model.toUpperCase() === model.toUpperCase()
                                );
                                
                                return (
                                  <button
                                    key={index}
                                    type="button"
                                    className={`w-full text-left px-3 py-2 hover:bg-gray-600 ${
                                      isConfigured ? 'text-gray-400 line-through' : 'text-white'
                                    }`}
                                    onClick={() => {
                                      if (!isConfigured) {
                                        setSelectedBikeModel(model);
                                        setDropdownOpen(false);
                                      }
                                    }}
                                    disabled={isConfigured}
                                  >
                                    {model}
                                    {isConfigured && (
                                      <span className="ml-2 text-xs text-orange-400">
                                        (already configured)
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <p className="text-gray-400 text-xs mt-1">
                          Models loaded from Firebase managedBikes collection
                        </p>
                      </div>
                      
                      {/* Price and Minimum Charge Inputs in a Row */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-gray-300 mb-1 text-sm">Price per km ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={newDeliveryPrice}
                            onChange={(e) => setNewDeliveryPrice(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white"
                            placeholder="0.00"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-gray-300 mb-1 text-sm">Minimum Charge ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={newMinimumCharge}
                            onChange={(e) => setNewMinimumCharge(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white"
                            placeholder="5.00"
                          />
                        </div>
                      </div>
                      
                      {/* Add Button */}
                      <div>
                        <button
                          type="button"
                          onClick={handleAddDeliveryPricing}
                          disabled={!selectedBikeModel || bikeModelsLoading}
                          className={`${
                            !selectedBikeModel || bikeModelsLoading
                              ? 'bg-gray-600 cursor-not-allowed' 
                              : 'bg-green-600 hover:bg-green-700'
                          } text-white h-10 px-4 rounded-lg transition-colors flex items-center justify-center w-full`}
                        >
                          <Plus size={18} className="mr-1" /> Add Pricing
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Existing delivery pricing */}
                <div>
                  <h3 className="text-white text-lg mb-3">Delivery Fees by Bike Model</h3>
                  
                  {config.deliveryPricing.length === 0 ? (
                    <div className="bg-gray-700 border border-gray-600 rounded-lg p-4 text-center">
                      <p className="text-gray-400">No delivery pricing configured yet.</p>
                      <p className="text-sm text-gray-400 mt-1">Add pricing for different bike models above.</p>
                    </div>
                  ) : (
                    <div className="bg-gray-700 border border-gray-600 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-gray-800">
                              <th className="px-4 py-3 text-left text-gray-300">Bike Model</th>
                              <th className="px-4 py-3 text-left text-gray-300">Price per km</th>
                              <th className="px-4 py-3 text-left text-gray-300">Minimum Charge</th>
                              <th className="px-4 py-3 text-right text-gray-300">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {config.deliveryPricing.map((pricing, index) => (
                              <tr key={index} className="border-t border-gray-600">
                                <td className="px-4 py-3 text-white">
                                  {pricing.model}
                                  {bikeModels.includes(pricing.model) && (
                                    <span className="ml-2 text-green-400 text-xs">✅</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-white">${pricing.pricePerKm.toFixed(2)}</td>
                                <td className="px-4 py-3 text-white">${(pricing.minimumCharge || 5).toFixed(2)}</td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveDeliveryPricing(index)}
                                    className="text-red-400 hover:text-red-300 transition-colors"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-800">
                  <p className="text-blue-300 text-sm">
                    <strong>How this works:</strong> When a customer selects a custom location, we'll calculate the distance 
                    between the rental office and the custom location. The delivery fee will be this distance multiplied by the 
                    configured price per kilometer for the selected bike model. If the calculated fee is less than the minimum 
                    charge, the minimum charge will be applied instead.
                  </p>
                  <p className="text-blue-200 text-xs mt-2">
                    ✅ <strong>Firebase Integration:</strong> Bike models are automatically synchronized with your managed bikes collection.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Analytics Configuration */}
          {activeTab === 'analytics' && (
            <div>
              <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                <BarChart size={20} className="mr-2 text-orange-500" />
                Analytics Configuration
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-gray-300 mb-2" htmlFor="gaPropertyId">
                    Google Analytics Property ID
                  </label>
                  <input
                    type="text"
                    id="gaPropertyId"
                    name="gaPropertyId"
                    value={config.gaPropertyId}
                    onChange={handleChange}
                    className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white"
                    placeholder="Enter your GA4 property ID"
                  />
                  <p className="text-gray-400 text-xs mt-1">
                    Your Google Analytics 4 property ID for analytics integration
                  </p>
                </div>

                <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                  <h3 className="text-white font-medium mb-2">Firebase Analytics Integration</h3>
                  <p className="text-gray-300 text-sm">
                    Analytics data is now stored directly in Firebase Firestore, providing real-time insights 
                    without the need for external analytics APIs.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Save Button (appears on all tabs) */}
          <div className="mt-8 pt-4 border-t border-gray-700 flex items-center">
            <button
              type="submit"
              disabled={!firebaseConnected || isSaving}
              className="bg-gradient-to-r from-orange-500 to-red-600 text-white py-2 px-6 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <span className="animate-spin h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></span>
                  Saving to Firebase...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save to Firebase
                </>
              )}
            </button>
            
            {saved && (
              <span className="ml-4 text-green-400 text-sm">
                Configuration saved to Firebase successfully!
              </span>
            )}
            
            {bikeModels.length > 0 && (
              <span className="ml-4 text-blue-400 text-sm">
                📊 {bikeModels.length} bike model{bikeModels.length !== 1 ? 's' : ''} loaded from Firebase
              </span>
            )}
          </div>
        </form>
      </div>
      
      <div className="mt-4 bg-gray-700 rounded-lg p-4 border border-gray-600 text-sm text-gray-300">
        <div className="flex items-center mb-2">
          <Database size={16} className="mr-2 text-orange-500" />
          <span className="font-medium">✅ Complete Firebase Integration</span>
        </div>
        <p>
          Configuration and bike models are stored securely in Firebase Firestore. Settings are synchronized across all devices 
          and bike models are automatically loaded from your managed bikes collection, ensuring delivery pricing stays current with your actual fleet.
        </p>
      </div>
    </div>
  );
};

export default SimpleConfigForm;